import React, { useState, useRef } from 'react';
import { Folder, Plus, Trash2, Layers, GripVertical } from 'lucide-react';
import { useFolderStore } from '../../stores/folderStore';
import { useProductStore } from '../../stores/productStore';
import { useAuthStore } from '../../stores/authStore';
import { getUserCreditsData } from '../../utils/supabase';
import { BulkImageImport } from './BulkImageImport';
import { TemplateManager } from '../templates/TemplateManager';
import { DeleteConfirmationModal } from '../common/DeleteConfirmationModal';

export function FolderList() {
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [draggedFolderId, setDraggedFolderId] = useState<number | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<number | null>(null);
  const [productCount, setProductCount] = useState(0);
  const [maxProducts, setMaxProducts] = useState(30);
  const user = useAuthStore(state => state.user);
  const products = useProductStore(state => state.products);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const folderIdRef = useRef<number | null>(null);
  const { 
    folders, 
    currentFolderId, 
    createFolder, 
    deleteFolder, 
    setCurrentFolder,
    reorderFolders
  } = useFolderStore();

  // 各フォルダの商品数をカウントする関数
  const getProductCountForFolder = (folderId: number | null) => {
    if (folderId === null) {
      return products.length; // すべての商品数
    }
    return products.filter(product => product.folderId === folderId).length;
  };

  // ユーザーの製品数と上限を取得（製品リストが変更されるたびに更新）
  const fetchProductLimits = React.useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const userCredits = await getUserCreditsData(user.id);
      if (userCredits) {
        setProductCount(userCredits.product_count || 0);
        setMaxProducts(userCredits.max_products || 30);
      } else {
        // Fallback values if fetch fails
        setProductCount(0);
        setMaxProducts(30);
      }
    } catch (error) {
      console.warn('Failed to fetch product limits, using fallback values:', error);
      // Set fallback values to prevent UI from breaking
      setProductCount(0);
      setMaxProducts(30);
    }
  }, [user?.id]);

  React.useEffect(() => {
    fetchProductLimits();
  }, [fetchProductLimits, products.length]); // products.lengthを依存配列に追加

  const handleCreateFolder = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    createFolder(newFolderName);
    setNewFolderName('');
    setIsCreating(false);
  };

  const handleDragStart = (e: React.DragEvent, folderId: number) => {
    setDraggedFolderId(folderId);
    e.dataTransfer.effectAllowed = 'move';
    
    // Create a custom drag image
    const dragPreview = document.createElement('div');
    dragPreview.className = 'bg-white p-2 rounded shadow-md border border-blue-300';
    const folder = folders.find(f => f.id === folderId);
    dragPreview.textContent = folder?.name || 'フォルダ';
    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 20, 20);
    
    // Remove the preview element after it's been used
    setTimeout(() => {
      document.body.removeChild(dragPreview);
    }, 0);
  };

  const handleDragOver = (e: React.DragEvent, folderId: number) => {
    e.preventDefault();
    
    const data = e.dataTransfer.getData('text/plain');
    if (data && (data.startsWith('product:') || data.startsWith('products:'))) {
      // Product(s) being dragged over folder
      setDragOverFolderId(folderId);
    }
    
    if (draggedFolderId === null || draggedFolderId === folderId) return;
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = () => {
    setDragOverFolderId(null);
  };

  const handleDrop = (e: React.DragEvent, targetFolderId: number) => {
    e.preventDefault();
    
    const data = e.dataTransfer.getData('text/plain');
    const [type, id] = data.split(':');
    
    if (type === 'product') {
      // Handle product drop
      const productId = parseInt(id);
      const product = products.find(p => p.id === productId);
      
      if (product && product.folderId !== targetFolderId) {
        const updatedProduct = {
          ...product,
          folderId: targetFolderId
        };
        useProductStore.getState().updateProduct(updatedProduct);
      }
    } else if (type === 'products') {
      // Handle multiple products drop
      const productIds = id.split(',').map(idStr => parseInt(idStr));
      const productsToMove = products.filter(p => productIds.includes(p.id));
      
      // 移動対象の商品をバッチで更新
      productsToMove.forEach(product => {
        if (product.folderId !== targetFolderId) {
          const updatedProduct = {
            ...product,
            folderId: targetFolderId
          };
          useProductStore.getState().updateProduct(updatedProduct);
        }
      });
      
      // 移動完了後、選択を解除
      useProductStore.getState().clearProductSelections();
    } else if (type === 'folder' && draggedFolderId !== null && draggedFolderId !== targetFolderId) {
      // Handle folder reordering
      const draggedIndex = folders.findIndex(f => f.id === draggedFolderId);
      const targetIndex = folders.findIndex(f => f.id === targetFolderId);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        reorderFolders(draggedIndex, targetIndex);
      }
    }
    
    setDraggedFolderId(null);
    setDragOverFolderId(null);
  };

  const handleDragEnd = () => {
    setDraggedFolderId(null);
    setDragOverFolderId(null);
  };

  const handleDeleteFolder = (folderId: number) => {
    const folder = folders.find(f => f.id === folderId);
    if (!folder) return;

    // Show confirmation modal before deletion
    setShowDeleteConfirmation(true);
    folderIdRef.current = folderId;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">フォルダ</h2>
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
          >
            <Plus className="w-4 h-4" />
            <span>新規フォルダ</span>
          </button>
        </div>

        <div className="space-y-1">
          <button
            onClick={() => setCurrentFolder(null)}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-left transition-colors ${
              currentFolderId === null
                ? 'bg-blue-50 text-blue-600'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDragLeave={handleDragLeave}
            onDrop={(e) => {
              e.preventDefault();
              const data = e.dataTransfer.getData('text/plain');
              const [type, id] = data.split(':');
              
              if (type === 'product') {
                const productId = parseInt(id);
                const product = products.find(p => p.id === productId);
                if (product && product.folderId !== null) {
                  const updatedProduct = { ...product, folderId: null };
                  useProductStore.getState().updateProduct(updatedProduct);
                }
              } else if (type === 'products') {
                const productIds = id.split(',').map(idStr => parseInt(idStr));
                const productsToMove = products.filter(p => productIds.includes(p.id));
                productsToMove.forEach(product => {
                  if (product.folderId !== null) {
                    const updatedProduct = { ...product, folderId: null };
                    useProductStore.getState().updateProduct(updatedProduct);
                  }
                });
                useProductStore.getState().clearProductSelections();
              }
              setDragOverFolderId(null);
            }}
          >
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4" />
              <span>すべてを表示</span>
            </div>
            <span className="text-xs text-gray-500">
              ({getProductCountForFolder(null)})
            </span>
          </button>
          
          {/* Product limit display */}
          <div className="px-3 py-2 text-sm text-gray-500">
            {productCount} / {maxProducts} 商品
          </div>
          
          <div className="mt-2 pt-2 border-t">
            {folders.map(folder => (
              <div
                key={folder.id}
                className={`flex items-center transition-colors ${
                  currentFolderId === folder.id
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                } ${dragOverFolderId === folder.id ? 'border-2 border-dashed border-blue-500 bg-blue-50 transform scale-105 shadow-lg' : ''} rounded-md group`}
                draggable
                onDragStart={(e) => handleDragStart(e, folder.id)}
                onDragOver={(e) => handleDragOver(e, folder.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, folder.id)}
                onDragEnd={handleDragEnd}
              >
                <div className="p-2 cursor-grab">
                  <GripVertical className="w-4 h-4 text-gray-400" />
                </div>
                <button
                  onClick={() => setCurrentFolder(folder.id)}
                  className="flex-1 flex items-center justify-between py-2 px-2 w-full"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Folder className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{folder.name}</span>
                  </div>
                  <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                    ({getProductCountForFolder(folder.id)})
                  </span>
                </button>
                {folder.name !== '基本フォルダ' && (
                  <button
                    onClick={() => handleDeleteFolder(folder.id)}
                    className="px-2 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {isCreating && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <form onSubmit={handleCreateFolder} className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="フォルダ名を入力"
                    className="w-full rounded-md border-gray-300"
                    autoFocus
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreating(false);
                      setNewFolderName('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 min-w-[90px]"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    disabled={!newFolderName.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed min-w-[90px]"
                  >
                    作成
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      <TemplateManager />

      {showDeleteConfirmation && (
        <DeleteConfirmationModal
          message={`フォルダを削除しますか？\nフォルダ内の商品は「すべてを表示」フォルダに移動されます。`}
          onConfirm={() => {
            deleteFolder(folderIdRef.current!);
            if (currentFolderId === folderIdRef.current) {
              setCurrentFolder(null); // フォルダ削除後に「すべてを表示」に切り替え
            }
            // 実際の削除処理は handleDeleteFolder 内で行う
            setShowDeleteConfirmation(false);
          }}
          onCancel={() => setShowDeleteConfirmation(false)}
        />
      )}

      <div className="border-t p-4">
        <BulkImageImport />
      </div>
    </div>
  );
}